<template>
    <tr>
        <table>
            <tr>
                <td>
                    <a
                        :href="DestinationTarget"
                        :data-lendername="offer.lenderShortName"
                        class="lender-logo"
                        target="_blank"
                        @click="trackRateTableClickedEvent('LogoClicked/')">
                        <img
                            :alt="offer.lenderShortName"
                            :src="offer.lenderLogo"
                        />
                    </a>
                </td>
                <td>
                    <span class="val-title">Interest Rate as low as</span>
                    <span class="values">{{ MinInterestRate }}%</span>
                </td>
                <td>
                    <span class="val-title">APR as low as</span>
                    <span class="values">{{ MinAPR }}%</span>
                </td>
                <td class="monthly-pay">
                    <span class="val-title">
                        <span>Est. Monthly Payment</span>
                        <lt-label
                            show-tooltip="true"
                            show-question-mark="true"
                            :popper-content="tooltipContent()"
                        />
                    </span>
                    <span class="values">${{ MonthlyPayment }}/mo.</span>
                </td>
                <td>
                    <span class="val-title">Loan Term</span>
                    <span class="values">{{ LoanProgram }}</span>
                </td>
                <td>
                    <div
                        class="offers-btn-block"
                        :data-lendername="offer.lenderShortName">
                        <a
                            :href="DestinationTarget"
                            target="_blank"
                            class="destination-btn"
                            @click="trackRateTableClickedEvent('ButtonClicked/')"> {{ destinationButtonText }}</a>
                    </div>
                </td>
            </tr>
        </table>
    </tr>
</template>

<script>
    import Accounting from 'accounting';
    import LtLabel from '~/components/lt-label.vue';
    import PaymentHelper from '~/helpers/payment';
    import wwwUrlEnv from '~/helpers/www-url-env';
    import RateTableInfo from '~/mixin/rate-table-offer-tracking';

    export default {
        components: {
            LtLabel,
        },
        mixins: [RateTableInfo],
        props: {
            offer: {
                default() {
                    return {};
                },
            },
            destinationButtonText: {
                default: 'Get Free Offers',
            },
            loanAmount: {
                default: 0,
            },
            zipCode: {
                default: '',
            },
            homeValue: {
                default: 0,
            },
            destinationLinkOrId: {
                default: '',
            },
            ctaParams: {
                default: '',
            },
        },
        computed: {
            DestinationTarget() {
                return this.getDestinationTarget(this.offer);
            },
            MinAPR() {
                return this.offer.minAPR.toFixed(2);
            },
            MinInterestRate() {
                return this.offer.minInterestRate.toFixed(2);
            },
            MonthlyPayment() {
                return this.getMonthlyPayment(this.offer);
            },
            LoanTermMonths() {
                return this.offer.loanTermMonths / 12;
            },
            LoanProgram() {
                return (this.offer?.loanProgram || '').toLowerCase().replace('arm', 'ARM');
            },
        },
        methods: {
            tooltipContent() {
                return `<div class="popup-content"><p>A $${this.MonthlyPayment} monthly payment assumes $${Accounting.formatNumber(this.loanAmount)} loan amount at ${this.offer.medianInterestRate.toFixed(2)}% (Median Interest Rate) over ${this.offer.loanTermMonths} months.</p></div>`;
            },
            getDestinationTarget(offer) {
                let splitterUrl = `${wwwUrlEnv.getWWWURL()}/redirect/offers?`;
                splitterUrl += `icid=ratetable&PropertyUse=${this.getTargetPropertyUse(offer.propertyUse)}&EstimatedCreditRating=${this.getTargetCreditRating(offer.selfGradedCredit)}&EstimatedPropertyValue=${this.homeValue}`;
                const loanType = offer.productCategory.toLowerCase();
                if (this.destinationLinkOrId === '') {
                    if (loanType === 'purchase') {
                        splitterUrl += '&id=wp-mortgage';
                    } else {
                        splitterUrl += `&id=wp-refinance&PropertyAddressZipCode=${this.zipCode}`;
                    }
                } else if (/^[A-z0-9-]+$/i.test(this.destinationLinkOrId)) {
                    splitterUrl += `&id=${this.destinationLinkOrId}`;
                } else {
                    splitterUrl = `${this.destinationLinkOrId}`;
                }

                if (this.ctaParams) {
                    splitterUrl += `&${this.ctaParams}`;
                }
                return splitterUrl;
            },
            getMonthlyPayment(offer) {
                const monthlyPayment = PaymentHelper.getMonthlyPrincipalAndIntreset(this.loanAmount, offer.loanTermMonths, PaymentHelper.convertInterestRatePerMonths(offer.medianInterestRate));
                return Accounting.formatNumber(monthlyPayment.toFixed(0));
            },
            getTargetPropertyUse(propertyUse) {
                switch (propertyUse.toLowerCase()) {
                    case 'primary home': return 'OWNEROCCUPIED';
                    case 'secondary home': return 'SECONDHOME';
                    case 'investment property': return 'INVESTMENTPROPERTY';
                    default: return '';
                }
            },
            getTargetCreditRating(creditBand) {
                switch (creditBand.toLowerCase()) {
                    case 'excellent': return 'EXCELLENT';
                    case 'good': return 'SOMECREDITPROBLEMS';
                    case 'fair': return 'MAJORCREDITPROBLEMS';
                    case 'poor': return 'LITTLEORNOCREDITHISTORY';
                    default: return '';
                }
            },
            trackRateTableClickedEvent(clicked) {
                this.ClickLocation = clicked;
                this.rateTableClickedInfo(this);
            },
        },
    };
</script>

<style lang="scss" scoped>
@import "../../../assets/styles/grove/grove-colors";
@import "../../../assets/styles/grove/buttons";

.val-title {
    white-space: nowrap;
    height: 16px;
    color: $text-gray !important;

    :deep(.popper) {
        white-space: normal;
    }

    :deep(.calc-question) {
        top: 0 !important;
        left: 0 !important;
        padding: 0 !important;
        margin: 0 0 0 3px !important;
    }
}

.values {
    color: $lt-blue-deep !important;
}

.destination-btn {
    @include button-common;
    @include button-size-medium;
    @include button-primary-blue;
    color: $white !important;
    width: 100%;
}
</style>
